// ATLAS · Endeavour survey — ORACLE's sector survey (same question banks,
// same "leansEndeavour" tally), with what the old one lacked: Back, Cancel at
// any point, a draft that survives leaving (AsyncStorage per user+sector), and
// a choice among the top suggestions instead of a single forced result.
// Optional — users who know their Endeavour never have to take it (spec §5).
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { Sector } from '../../domain/olmps';
import { SECTOR_LABELS } from '../../domain/olmps';
import { SECTOR_BANKS } from '../../olmps/question-banks';
import { Chip, GlassCard, GoldButton, Kicker, Track } from '../../v10/ui';
import { useTheme, makeThemedStyles, DISPLAY_FONT } from '../../v10/theme';

const MIN_Q = 5;
const draftKey = (userId: string, sector: Sector) => `atlas.survey.${userId}.${sector}`;

/** answers[i] = the leansEndeavour of the option picked for question i. */
type Draft = { answers: string[] };

export function AtlasSurvey({
  userId,
  sector,
  onCancel,
  onPick,
}: {
  userId: string;
  sector: Sector;
  onCancel: () => void;
  onPick: (endeavour: string) => void;
}) {
  const styles = useStyles();
  const { t } = useTheme();
  const questions = SECTOR_BANKS[sector].questions;
  const maxQ = Math.min(20, questions.length);
  const [answers, setAnswers] = useState<string[] | null>(null);
  const [showResults, setShowResults] = useState(false);

  // Resume a saved draft.
  useEffect(() => {
    let alive = true;
    AsyncStorage.getItem(draftKey(userId, sector))
      .then((raw) => {
        if (!alive) return;
        const d = raw ? (JSON.parse(raw) as Draft) : null;
        setAnswers(Array.isArray(d?.answers) ? d!.answers.slice(0, maxQ) : []);
      })
      .catch(() => alive && setAnswers([]));
    return () => {
      alive = false;
    };
  }, [userId, sector, maxQ]);

  useEffect(() => {
    if (answers === null) return;
    AsyncStorage.setItem(draftKey(userId, sector), JSON.stringify({ answers } satisfies Draft)).catch(() => {});
  }, [answers, userId, sector]);

  const ranked = useMemo(() => {
    const tally: Record<string, number> = {};
    for (const a of answers ?? []) tally[a] = (tally[a] || 0) + 1;
    return Object.entries(tally).sort((x, y) => y[1] - x[1]);
  }, [answers]);

  if (answers === null) return null;

  const idx = answers.length;
  const done = showResults || idx >= maxQ;
  const clearDraft = () => AsyncStorage.removeItem(draftKey(userId, sector)).catch(() => {});

  if (done) {
    const top = ranked.slice(0, 3);
    return (
      <GlassCard style={styles.card}>
        <Kicker>{SECTOR_LABELS[sector]} · Survey result</Kicker>
        <Text style={styles.prompt}>Based on {idx} answers, these Endeavours fit you best. Pick one — you can edit it after.</Text>
        {top.length === 0 ? <Text style={styles.muted}>No answers yet.</Text> : null}
        {top.map(([e, n]) => (
          <GoldButton
            key={e}
            ghost
            label={`${e}  ·  ${n}`}
            onPress={() => {
              clearDraft();
              onPick(e);
            }}
            style={styles.option}
          />
        ))}
        <View style={styles.row}>
          {idx < maxQ ? <GoldButton compact ghost label="Keep answering" onPress={() => setShowResults(false)} /> : null}
          <GoldButton compact ghost label="Start over" onPress={() => { setAnswers([]); setShowResults(false); }} />
          <GoldButton compact ghost label="Close" onPress={onCancel} />
        </View>
      </GlassCard>
    );
  }

  const q = questions[idx];
  return (
    <GlassCard style={styles.card}>
      <View style={styles.head}>
        <Kicker>{SECTOR_LABELS[sector]} · Question {idx + 1} of {maxQ}</Kicker>
        <Chip small label="Cancel" onPress={onCancel} />
      </View>
      <Track pct={(idx + 1) / maxQ} style={{ marginVertical: 8 }} />
      <Text style={styles.prompt}>{q.prompt}</Text>
      {q.options.map((o) => (
        <GoldButton key={o.label} ghost label={o.label} onPress={() => setAnswers([...answers, o.leansEndeavour])} style={styles.option} />
      ))}
      <View style={styles.row}>
        <GoldButton compact ghost disabled={idx === 0} label="‹ Back" onPress={() => setAnswers(answers.slice(0, -1))} />
        {idx >= MIN_Q ? <GoldButton compact label="See suggestions" onPress={() => setShowResults(true)} /> : (
          <Text style={[styles.muted, { color: t.ivoryFaint }]}>Suggestions after {MIN_Q} answers · saved as you go</Text>
        )}
      </View>
    </GlassCard>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    card: { padding: 16, gap: 8 },
    head: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    prompt: { fontFamily: DISPLAY_FONT, fontSize: 17, lineHeight: 23, color: t.ivory, marginVertical: 4 },
    option: { alignItems: 'flex-start' },
    row: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 6 },
    muted: { fontSize: 12, color: t.ivorySoft },
  }),
);
